import React, { useState } from "react";
import { FiChevronLeft, FiChevronRight, FiMenu } from "react-icons/fi";
import { useDispatch } from "react-redux";
import { TermsAndConditionModal } from "components/SignUpForm/SignupTermsModal";
import { acceptTermsAndConditions } from "actions/authActions";
import { showToast } from "utils/helpers";
import { useCurrentUser } from "hooks/auth";
import { FeatureFlagType } from "newui/features/Auth/types/FeatureFlag.type";
import { useProfileContext } from "newui/features/Auth/context/ProfileContext";
import { AppDispatch } from "newui/store";
import { AppMenuClient } from "./components/AppMenuClient";
import { AppMenuSupplier } from "./components/AppMenuSupplier";
import { AppMenuGuest } from "./components/AppMenuGuest";
import { useAppMenu } from "./hooks/useAppMenu";

interface AppMenuProps {
  isDefaultCollapsed?: boolean;
}

export const AppMenu: React.FC<AppMenuProps> = ({ isDefaultCollapsed = false }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { isCollapsed: isMenuCollapsed, collapse } = useAppMenu();
  const isCollapsed = isMenuCollapsed ?? isDefaultCollapsed;
  const [isLoading, setIsLoading] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const { isClientProfileActive } = useProfileContext();
  const { isGuest, currentUser } = useCurrentUser();

  const showTermsAndConditionsModal = (): boolean => {
    if (isGuest) {
      return false;
    }
    const featureFlags = currentUser?.feature_flags;
    return !featureFlags?.some(
      (obj: any) => obj.id === FeatureFlagType.TERMS_AND_CONDITIONS
    );
  };

  const handleAcceptTermsAndConditions = async () => {
    try {
      setIsLoading(true);
      await dispatch(acceptTermsAndConditions());
      setIsLoading(false);
      showToast("Terms and Conditions accepted", "Success", true);
    } catch (error) {
      console.error(error);
      setIsLoading(false);
      showToast("Failed to accept terms and conditions", "Error", false);
    }
  };

  const handleNotchClick = () => {
    collapse(!isCollapsed);
  };

  const renderMenuVariant = (onNavigate?: () => void) => {
    if (isClientProfileActive) {
      return (
        <AppMenuClient
          isCollapsed={isCollapsed}
          onNavigate={onNavigate}
        />
      );
    }
    if (isGuest) {
      return (
        <AppMenuGuest
          isCollapsed={isCollapsed}
          onNavigate={onNavigate}
        />
      );
    }
    return (
      <AppMenuSupplier
        isCollapsed={isCollapsed}
        onNavigate={onNavigate}
      />
    );
  };

  const sidebarWidth = isCollapsed ? "w-[68px]" : "w-[300px]";

  return (
    <>
      {/* Mobile top bar */}
      <div className="fixed left-0 right-0 top-0 z-40 flex h-14 items-center bg-white px-4 shadow-sm md:hidden">
        <button
          onClick={() => setIsMobileOpen(true)}
          aria-label="Open menu"
          className="flex h-9 w-9 items-center justify-center rounded-md hover:bg-utility-blue-light-50"
        >
          <FiMenu size={22} />
        </button>
      </div>

      {/* Mobile drawer overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 z-50 md:hidden"
          onClick={() => setIsMobileOpen(false)}
        >
          <div className="absolute inset-0 bg-black/40" />
          <div
            className="absolute left-0 top-0 h-full w-[320px] overflow-y-auto bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {renderMenuVariant(() => setIsMobileOpen(false))}
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div
        className={`relative hidden md:block transition-all duration-300 ${sidebarWidth}`}
        style={{
          height: "max-content",
          position: "sticky",
          top: 0,
          zIndex: 2,
        }}
      >
        {/* Collapse notch — absolute child so it tracks the sidebar right edge as width transitions */}
        <div
          id="notch"
          className={`z-[99999] flex cursor-pointer items-center justify-center rounded-md transition-all duration-300 ${isCollapsed
            ? "w-[26px] bg-royal-blue hover:w-[36px]"
            : "w-[26px] border border-l-0 border-gray-200 bg-white hover:w-[36px] hover:bg-utility-blue-light-50"
            }`}
          style={{
            position: "absolute",
            ...(isCollapsed ? { left: "calc(100% - 13px)" } : { right: "-13px" }),
            top: "50px",
            height: "44px",
            zIndex: 10,
            boxShadow: "3px 2px 8px rgba(16, 24, 40, 0.18)",
          }}
          onClick={handleNotchClick}
          role="button"
          aria-label={isCollapsed ? "Expand menu" : "Collapse menu"}
        >
          {isCollapsed ? (
            <FiChevronRight style={{ color: "#fff" }} />
          ) : (
            <FiChevronLeft className="text-royal-blue" />
          )}
        </div>

        <div
          className={`${isCollapsed ? "collapsed" : ""}`}
          style={{
            height: "100vh",
            background: isCollapsed ? "#003ce9" : "#fff",
            borderRight: isCollapsed ? "none" : "1px solid #003ce9",
            boxShadow: isCollapsed ? "none" : "5px 0 16px rgba(16, 24, 40, 0.08)",
            overflowY: "auto",
            width: isCollapsed ? "68px" : "300px",
            transition: "all 0.3s ease-in-out",
          }}
        >
          {renderMenuVariant()}
        </div>

        <TermsAndConditionModal
          isModalOpen={showTermsAndConditionsModal()}
          handleOnAccept={handleAcceptTermsAndConditions}
          handleClose={null}
          showCloseButton={false}
          isLoading={isLoading}
        />
      </div>

      {/* Mobile top bar spacer */}
      <div className="h-14 md:hidden" />
    </>
  );
};
